/**
 * WorkspaceMembersList Component - Member Management List
 *
 * Displays paginated list of workspace members with role management.
 *
 * @module components/workspace/WorkspaceMembersList
 */

import { useState, memo, useCallback } from 'react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Group,
  Indicator,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconCrown, IconTrash, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PaginatedList } from '@/components/ui/PaginatedList';
import { SearchAndFilters } from '@/components/ui/SearchAndFilters';
import { FilterPill } from '@/components/ui/FilterPill';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { RoleSelector } from '@/components/common/RoleSelector';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { getAvatarColor, getEmailInitials } from '@/constants/roles';
import {
  ICON_SIZE_TAB,
  ICON_SIZE_SM,
  ICON_SIZE_EMPTY_STATE,
  MEMBER_DETAILS_GAP,
  FONT_WEIGHT_MEDIUM,
  TEAM_PANEL_LIST_MAX_HEIGHT,
} from '@/constants/ui';
import { canManageMembers } from '@/types/workspace';
import type { WorkspaceMember, WorkspaceRole } from '@/types/workspace';
import type { PresenceStatus } from '@/store/slices/presenceSlice';
import styles from './WorkspaceMembersList.module.css';

interface WorkspaceMembersListProps {
  /** List of members */
  members: WorkspaceMember[];
  /** Whether there are more members to load */
  hasNextPage: boolean;
  /** Whether members are loading */
  isLoading: boolean;
  /** Whether the members list has completed at least one fetch */
  hasLoaded: boolean;
  /** Search value */
  search: string;
  /** Active role filter */
  roleFilter: WorkspaceRole | null;
  /** Current user's role in workspace */
  currentUserRole: WorkspaceRole;
  /** Current user's ID */
  currentUserId: string;
  /** Workspace owner's ID for identifying owner in member list (optional) */
  workspaceOwnerId?: string;
  /** Callback when search changes */
  onSearchChange: (search: string) => void;
  /** Callback when role filter changes */
  onRoleFilterChange: (role: WorkspaceRole | null) => void;
  /** Callback to load more members */
  onLoadMore: () => void;
  /** Callback when role is changed */
  onRoleChange: (userId: string, newRole: WorkspaceRole) => Promise<boolean>;
  /** Callback when member is removed */
  onRemove: (userId: string) => Promise<boolean>;
  /** Status of users (online/away) */
  userStatuses?: Record<string, PresenceStatus>;
  /** Error message */
  error?: string | null;
}

const ROLE_OPTIONS: WorkspaceRole[] = ['Admin', 'Editor', 'Viewer'];

/** Friendly label for a member: display name, else email local-part, else email. */
const getMemberDisplayName = (
  member: Pick<WorkspaceMember, 'displayName' | 'email'>
): string => member.displayName ?? member.email.split('@')[0] ?? member.email;

export const WorkspaceMembersList = memo(function WorkspaceMembersList({
  members,
  hasNextPage,
  isLoading,
  hasLoaded,
  search,
  roleFilter,
  currentUserRole,
  currentUserId,
  workspaceOwnerId,
  onSearchChange,
  onRoleFilterChange,
  onLoadMore,
  onRoleChange,
  onRemove,
  userStatuses,
  error,
}: WorkspaceMembersListProps) {
  const { t } = useTranslation();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(
    null
  );

  const canManage = canManageMembers(currentUserRole);
  const roleOptions = canManage
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((role) => role === 'Admin');
  const hasActiveFilters = roleFilter !== null;

  const handleFilterToggle = useCallback(() => {
    setFiltersExpanded((prev) => !prev);
  }, []);

  const handleRoleFilterChange = useCallback(
    (value: string | null) => {
      onRoleFilterChange(
        value === 'all' ? null : (value as WorkspaceRole | null)
      );
    },
    [onRoleFilterChange]
  );

  const clearRoleFilter = useCallback(() => {
    onRoleFilterChange(null);
  }, [onRoleFilterChange]);

  const getRoleLabel = useCallback(
    (role: WorkspaceRole) => t(`workspace.roles.${role.toLowerCase()}`),
    [t]
  );

  const renderMember = useCallback(
    (member: WorkspaceMember, _index: number, _isFocused: boolean) => {
      const isMemberOwner = member.userId === workspaceOwnerId;
      const isSelf = member.userId === currentUserId;
      const canModify = canManage && !isMemberOwner && !isSelf;

      function handleRemove() {
        setMemberToRemove(member);
      }

      const displayName = getMemberDisplayName(member);

      return (
        <Group
          className={styles.memberRow}
          justify="space-between"
          wrap="nowrap"
        >
          <Group gap="sm" wrap="nowrap" className={styles.memberInfo}>
            <Indicator
              color={
                userStatuses?.[member.userId] === 'away'
                  ? 'var(--app-status-delayed)'
                  : 'var(--app-status-completed)'
              }
              position="bottom-end"
              size={10}
              offset={2}
              withBorder
              disabled={!userStatuses?.[member.userId]}
              processing={false}
            >
              <Avatar
                src={member.avatarUrl ?? undefined}
                alt={displayName}
                radius="xl"
                size="md"
                color={getAvatarColor(member.email)}
              >
                {member.avatarUrl ? undefined : getEmailInitials(member.email)}
              </Avatar>
            </Indicator>

            <Stack gap={MEMBER_DETAILS_GAP} className={styles.memberDetails}>
              <Group gap="xs" wrap="nowrap">
                <Text fw={FONT_WEIGHT_MEDIUM} truncate>
                  {displayName}
                </Text>
                {isMemberOwner && (
                  <IconCrown
                    size={ICON_SIZE_TAB}
                    className={styles.ownerIcon}
                  />
                )}
                {isSelf && (
                  <Badge size="xs" variant="outline">
                    {t('workspace.members.you')}
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed" truncate>
                {member.email}
              </Text>
            </Stack>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {canModify ? (
              <>
                <Box component="span" className={styles.roleBadgeDefault}>
                  <StatusBadge type="role" value={member.role} size="sm" />
                </Box>
                <Box className={styles.hoverControls}>
                  <Group gap="xs" wrap="nowrap">
                    <RoleSelector
                      variant="badge"
                      size="sm"
                      value={member.role}
                      onChange={(newRole) => {
                        if (newRole !== member.role) {
                          void onRoleChange(member.userId, newRole);
                        }
                      }}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={handleRemove}
                      title={t('workspace.members.remove')}
                      className={styles.removeAction}
                    >
                      <IconTrash size={ICON_SIZE_SM} />
                    </ActionIcon>
                  </Group>
                </Box>
              </>
            ) : (
              (isMemberOwner || member.role === 'Admin' || isSelf) && (
                <StatusBadge type="role" value={member.role} size="sm" />
              )
            )}
          </Group>
        </Group>
      );
    },
    [canManage, currentUserId, workspaceOwnerId, userStatuses, onRoleChange, t]
  );

  const keyExtractor = useCallback(
    (member: WorkspaceMember, _index: number) => member.userId,
    []
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!memberToRemove) return;
    await onRemove(memberToRemove.userId);
    setMemberToRemove(null);
  }, [memberToRemove, onRemove]);

  const handleCancelRemove = useCallback(() => setMemberToRemove(null), []);

  return (
    <Stack gap="md">
      <SearchAndFilters variant="compact">
        <SearchAndFilters.Header>
          <SearchAndFilters.SearchBar
            placeholder={t('workspace.members.searchPlaceholder')}
            value={search}
            onChange={onSearchChange}
          />
          <SearchAndFilters.FilterToggle
            expanded={filtersExpanded}
            hasActiveFilters={hasActiveFilters}
            onClick={handleFilterToggle}
          />
        </SearchAndFilters.Header>

        <SearchAndFilters.Section
          hasContent={hasActiveFilters || filtersExpanded}
        >
          <SearchAndFilters.Pills show={!filtersExpanded && hasActiveFilters}>
            {roleFilter && (
              <FilterPill
                label={t('workspace.filters.role')}
                value={getRoleLabel(roleFilter)}
                onRemove={clearRoleFilter}
                ariaLabel={t('workspace.filters.clearRole')}
              />
            )}
          </SearchAndFilters.Pills>
          <SearchAndFilters.FiltersPanel expanded={filtersExpanded}>
            <Select
              label={t('workspace.filters.role')}
              data={[
                { value: 'all', label: t('workspace.filters.allRoles') },
                ...roleOptions.map((role) => ({
                  value: role,
                  label: getRoleLabel(role),
                })),
              ]}
              value={roleFilter ?? 'all'}
              onChange={handleRoleFilterChange}
              size="sm"
              comboboxProps={{ withinPortal: true }}
            />
          </SearchAndFilters.FiltersPanel>
        </SearchAndFilters.Section>
      </SearchAndFilters>

      <AppScrollArea maxHeight={TEAM_PANEL_LIST_MAX_HEIGHT} clampWidth>
        <PaginatedList
          data={members}
          renderItem={renderMember}
          keyExtractor={keyExtractor}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          hasLoaded={hasLoaded}
          onLoadMore={onLoadMore}
          error={error}
          stateRenderers={{
            empty: () => (
              <EmptyState
                title={t('workspace.members.emptyTitle')}
                description={t('workspace.members.emptyHint')}
                icon={<IconUsers size={ICON_SIZE_EMPTY_STATE} />}
                size="md"
              />
            ),
          }}
          styling={{
            gap: 'xs',
          }}
        />
      </AppScrollArea>

      <ConfirmationModal
        opened={memberToRemove !== null}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        variant="danger"
        title={t('workspace.members.removeMember')}
        message={t('workspace.members.confirmRemove', {
          name: memberToRemove ? getMemberDisplayName(memberToRemove) : '',
        })}
        confirmLabel={t('workspace.members.remove')}
      />
    </Stack>
  );
});
